<script setup>
import { useTeletelFeatures } from 'dashboard/composables/useTeletelFeatures';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useFunctionGetter, useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import EmailTranscriptModal from 'dashboard/components/widgets/conversation/EmailTranscriptModal.vue';
import ReminderDialog from 'dashboard/components/widgets/conversation/ReminderDialog.vue';
import RemindersAPI from 'dashboard/api/teletel/reminders';
import { emitter } from 'shared/helpers/mitt';

import SidebarActionsHeader from 'dashboard/components-next/SidebarActionsHeader.vue';
import EditContact from './contact/EditContact.vue';
import ConversationAction from './ConversationAction.vue';
import ConversationInfo from './ConversationInfo.vue';
import ConversationParticipant from './ConversationParticipant.vue';
import ContactNotes from './contact/ContactNotes.vue';
import MacrosList from './Macros/List.vue';
import CustomAttributes from './customAttributes/CustomAttributes.vue';
import SharedFiles from './SharedFiles.vue';
import ContactConversations from './ContactConversations.vue';
import LinearIssuesList from 'dashboard/components/widgets/conversation/linear/IssuesList.vue';
import ShopifyOrdersList from 'dashboard/components/widgets/conversation/ShopifyOrdersList.vue';

const { isTeletelFeatureEnabled } = useTeletelFeatures();

const store = useStore();
const { t } = useI18n();

const { uiSettings, updateUISettings, conversationSidebarItemsOrder } =
  useUISettings();
const currentChat = useMapGetter('getSelectedChat');

const SECTION_RAIL = {
  conversation_actions: {
    icon: 'i-lucide-sliders-horizontal',
    titleKey: 'CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_ACTIONS',
  },
  conversation_participants: {
    icon: 'i-lucide-users',
    titleKey: 'CONVERSATION_PARTICIPANTS.SIDEBAR_TITLE',
  },
  conversation_info: {
    icon: 'i-lucide-info',
    titleKey: 'CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_INFO',
  },
  contact_attributes: {
    icon: 'i-lucide-tag',
    titleKey: 'CONVERSATION_SIDEBAR.ACCORDION.CONTACT_ATTRIBUTES',
  },
  previous_conversation: {
    icon: 'i-lucide-history',
    titleKey: 'CONVERSATION_SIDEBAR.ACCORDION.PREVIOUS_CONVERSATION',
  },
  macros: {
    icon: 'i-lucide-zap',
    titleKey: 'CONVERSATION_SIDEBAR.ACCORDION.MACROS',
  },
  linear_issues: {
    icon: 'i-lucide-circle-dot',
    titleKey: 'CONVERSATION_SIDEBAR.ACCORDION.LINEAR_ISSUES',
  },
  shopify_orders: {
    icon: 'i-lucide-shopping-bag',
    titleKey: 'CONVERSATION_SIDEBAR.ACCORDION.SHOPIFY_ORDERS',
  },
  contact_notes: {
    icon: 'i-lucide-notebook-pen',
    titleKey: 'CONVERSATION_SIDEBAR.ACCORDION.CONTACT_NOTES',
  },
  shared_files: {
    icon: 'i-lucide-paperclip',
    titleKey: 'CONVERSATION_SIDEBAR.ACCORDION.SHARED_FILES',
  },
};

// Owner directive 2026-09-14: every rail control opens the same kind of side
// panel the contact button opens (a static column next to the rail), not a
// modal here, a popover there and a drawer elsewhere. One panel, one key.
const activePanel = ref(null); // 'edit_contact' | 'reminders' | 'transcript' | <rail item name>
const closeRailPanel = () => {
  activePanel.value = null;
};
const openRailPanel = key => {
  if (activePanel.value === key) {
    closeRailPanel();
    return;
  }
  activePanel.value = key;
  updateUISettings({
    is_contact_sidebar_open: false,
    is_copilot_panel_open: false,
  });
};
const railPanelTitle = computed(() => {
  const map = {
    edit_contact: 'EDIT_CONTACT.BUTTON_LABEL',
    reminders: 'TELETEL_REMINDERS.DIALOG.TITLE',
    transcript: 'CONTACT_PANEL.SEND_TRANSCRIPT',
  };
  if (map[activePanel.value]) return t(map[activePanel.value]);
  const section = SECTION_RAIL[activePanel.value];
  return section ? t(section.titleKey) : '';
});
watch(
  () => currentChat.value?.id,
  () => closeRailPanel()
);

const showEditModal = computed(() => activePanel.value === 'edit_contact');

const showEmailActionsModal = computed(
  () => activePanel.value === 'transcript'
);
const toggleEmailModal = () => openRailPanel('transcript');

const isContactSidebarOpen = computed(
  () => uiSettings.value.is_contact_sidebar_open
);
const isCopilotPanelOpen = computed(
  () => uiSettings.value.is_copilot_panel_open
);

// Teletel's agent copilot as a side-panel tab, where Captain's used to be.
// The rail button flips the same ui flag; the copilot container renders as a
// static column next to the conversation while it is on.
const showCopilotTab = computed(() =>
  Boolean(isTeletelFeatureEnabled('assistant'))
);
const handleCopilotToggle = () => {
  closeRailPanel();
  updateUISettings({
    is_contact_sidebar_open: false,
    is_copilot_panel_open: !isCopilotPanelOpen.value,
  });
};

const handleContactToggle = () => {
  closeRailPanel();
  if (isCopilotPanelOpen.value) {
    updateUISettings({
      is_contact_sidebar_open: true,
      is_copilot_panel_open: false,
    });
  } else {
    updateUISettings({
      is_contact_sidebar_open: !isContactSidebarOpen.value,
      is_copilot_panel_open: false,
    });
  }
};

// Reminders moved here from the header (owner): one rail control under
// Macros, with the count badge that turns red the moment one is due.
const showReminderDialog = computed(() => activePanel.value === 'reminders');
const reminderCount = ref(0);
const reminderDueCount = ref(0);
const loadReminderCount = async () => {
  if (!currentChat.value?.id) return;
  try {
    const { data } = await RemindersAPI.forConversation(currentChat.value.id);
    reminderCount.value = data.payload.length;
    reminderDueCount.value = data.payload.filter(
      reminder => new Date(reminder.remind_at) <= new Date()
    ).length;
  } catch {
    reminderCount.value = 0;
    reminderDueCount.value = 0;
  }
};
watch(() => currentChat.value?.id, loadReminderCount, { immediate: true });
onMounted(() => {
  emitter.on('teletelReminderChanged', loadReminderCount);
  emitter.on('teletelReminderDue', loadReminderCount);
});
onBeforeUnmount(() => {
  emitter.off('teletelReminderChanged', loadReminderCount);
  emitter.off('teletelReminderDue', loadReminderCount);
});

const isMuted = computed(() => !!currentChat.value?.muted);

const toggleMute = () => {
  if (isMuted.value) {
    store.dispatch('unmuteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
  } else {
    store.dispatch('muteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
  }
};

const shopifyIntegration = useFunctionGetter(
  'integrations/getIntegration',
  'shopify'
);
const isShopifyFeatureEnabled = computed(
  () => shopifyIntegration.value.enabled
);

const { isCloudFeatureEnabled } = useAccount();
const isLinearFeatureEnabled = computed(() =>
  isCloudFeatureEnabled(FEATURE_FLAGS.LINEAR)
);
const linearIntegration = useFunctionGetter(
  'integrations/getIntegration',
  'linear'
);
const isLinearClientIdConfigured = computed(
  () => !!linearIntegration.value?.id
);

const conversationMetadataGetter = useMapGetter(
  'conversationMetadata/getConversationMetadata'
);
const currentConversationMetaData = computed(() =>
  conversationMetadataGetter.value(currentChat.value?.id)
);
const conversationAdditionalAttributes = computed(
  () => currentConversationMetaData.value?.additional_attributes || {}
);

const contactGetter = useMapGetter('contacts/getContact');
const contactId = computed(() => currentChat.value?.meta?.sender?.id);
const contact = computed(() => contactGetter.value(contactId.value) || {});
const contactAdditionalAttributes = computed(
  () => contact.value?.additional_attributes || {}
);

const HIDDEN_SIDEBAR_ITEMS = ['conversation_actions', 'conversation_info'];

// Owner directive: the rail carries one popover control, Macros. The rest of
// the panels are still built below and reachable from the contact sidebar, so
// re-listing a name here brings its icon straight back.
const RAIL_ITEMS_SHOWN = ['macros'];

const railItems = computed(() =>
  conversationSidebarItemsOrder.value
    .filter(item => !HIDDEN_SIDEBAR_ITEMS.includes(item.name))
    .filter(item => RAIL_ITEMS_SHOWN.includes(item.name))
    .filter(item => SECTION_RAIL[item.name])
    .filter(item => {
      if (item.name === 'linear_issues')
        return isLinearFeatureEnabled.value && isLinearClientIdConfigured.value;
      if (item.name === 'shopify_orders') return isShopifyFeatureEnabled.value;
      return true;
    })
    .map(item => ({ name: item.name, ...SECTION_RAIL[item.name] }))
);
</script>

<template>
  <!-- The copilot column lands here (Teleport from TeletelCopilotContainer),
       so it shares this exact slot with the rail's other panels. -->
  <div id="teletel-copilot-rail-slot" class="contents" />
  <!-- The rail's side panel: same column the contact panel uses. -->
  <div
    v-if="activePanel"
    class="bg-n-surface-2 h-full overflow-hidden flex flex-col fixed top-0 z-40 w-full max-w-sm ltr:right-0 rtl:left-0 md:static md:w-[320px] md:min-w-[320px] ltr:border-l rtl:border-r border-n-weak 2xl:min-w-[360px] 2xl:w-[360px] shadow-lg md:shadow-none"
  >
    <!-- Owner order 2026-09-16: one header for every side panel (contact,
         rail panels, assistant) so the close button never moves. -->
    <SidebarActionsHeader :title="railPanelTitle" @close="closeRailPanel" />
    <div class="flex flex-1 overflow-auto flex-col">
      <EditContact
        v-if="showEditModal && contact && contact.id"
        embedded
        :show="showEditModal"
        :contact="contact"
        @cancel="closeRailPanel"
      />
      <ReminderDialog
        v-else-if="showReminderDialog && currentChat.id"
        embedded
        :show="showReminderDialog"
        :conversation-id="currentChat.id"
        @close="closeRailPanel"
      />
      <EmailTranscriptModal
        v-else-if="showEmailActionsModal"
        embedded
        :show="showEmailActionsModal"
        :current-chat="currentChat"
        @cancel="closeRailPanel"
      />
      <template v-for="item in railItems" :key="`panel-${item.name}`">
        <div v-if="activePanel === item.name" class="p-3">
          <!-- conversation_actions -->
          <ConversationAction
            v-if="item.name === 'conversation_actions'"
            :conversation-id="currentChat.id"
            :inbox-id="currentChat.inbox_id"
          />

          <!-- conversation_info -->
          <ConversationInfo
            v-else-if="item.name === 'conversation_info'"
            :conversation-attributes="conversationAdditionalAttributes"
            :contact-attributes="contactAdditionalAttributes"
          />

          <!-- conversation_participants -->
          <ConversationParticipant
            v-else-if="item.name === 'conversation_participants'"
            :conversation-id="currentChat.id"
            :inbox-id="currentChat.inbox_id"
          />

          <!-- contact_notes -->
          <ContactNotes
            v-else-if="item.name === 'contact_notes'"
            :contact-id="contactId"
          />

          <!-- macros -->
          <MacrosList
            v-else-if="item.name === 'macros'"
            :conversation-id="currentChat.id"
          />

          <!-- contact_attributes -->
          <CustomAttributes
            v-else-if="item.name === 'contact_attributes'"
            attribute-type="contact_attribute"
            attribute-from="conversation_contact_panel"
            :contact-id="contact.id"
            :empty-state-message="
              $t('CONVERSATION_CUSTOM_ATTRIBUTES.NO_RECORDS_FOUND')
            "
          />

          <!-- shared_files -->
          <SharedFiles
            v-else-if="item.name === 'shared_files'"
            :conversation-id="currentChat.id"
          />

          <!-- previous_conversation -->
          <ContactConversations
            v-else-if="item.name === 'previous_conversation'"
            :contact-id="contact.id"
            :conversation-id="currentChat.id"
          />

          <!-- linear_issues -->
          <LinearIssuesList
            v-else-if="item.name === 'linear_issues'"
            :conversation-id="currentChat.id"
          />

          <!-- shopify_orders -->
          <ShopifyOrdersList
            v-else-if="item.name === 'shopify_orders'"
            :conversation-id="currentChat.id"
          />
        </div>
      </template>
    </div>
  </div>
  <nav
    class="flex flex-col items-center justify-between gap-1 pt-3 pb-3 px-1 shrink-0 h-full overflow-y-auto bg-n-surface-2 ltr:border-l rtl:border-r border-n-weak"
    :aria-label="$t('CONVERSATION.SIDEBAR.CONTACT')"
  >
    <div class="flex flex-col items-center gap-1 w-full">
      <button
        type="button"
        class="flex shrink-0 items-center justify-center rounded-lg p-0 size-8 xl:size-10 text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12 transition-colors duration-150"
        :class="{
          'bg-n-alpha-2 text-n-slate-12 font-medium shadow-sm':
            isContactSidebarOpen && !isCopilotPanelOpen,
        }"
        :title="$t('CONVERSATION.SIDEBAR.CONTACT')"
        :aria-label="$t('CONVERSATION.SIDEBAR.CONTACT')"
        @click="handleContactToggle"
      >
        <span class="i-ph-user-bold size-4 shrink-0" aria-hidden="true" />
      </button>
      <button
        type="button"
        class="flex shrink-0 items-center justify-center rounded-lg p-0 size-8 xl:size-10 text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12 transition-colors duration-150"
        :class="{
          'bg-n-alpha-2 text-n-slate-12 font-medium shadow-sm': showEditModal,
        }"
        :title="$t('EDIT_CONTACT.BUTTON_LABEL')"
        :aria-label="$t('EDIT_CONTACT.BUTTON_LABEL')"
        @click="openRailPanel('edit_contact')"
      >
        <span class="i-ph-pencil-simple size-4 shrink-0" aria-hidden="true" />
      </button>
      <button
        v-if="showCopilotTab"
        type="button"
        class="flex shrink-0 items-center justify-center rounded-lg p-0 size-8 xl:size-10 text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12 transition-colors duration-150"
        :class="{
          'bg-n-alpha-2 !text-n-blue-11 shadow-sm': isCopilotPanelOpen,
        }"
        :title="$t('TELETEL_COPILOT.TITLE')"
        :aria-label="$t('TELETEL_COPILOT.TITLE')"
        @click="handleCopilotToggle"
      >
        <span class="i-lucide-bot size-4 shrink-0" aria-hidden="true" />
      </button>
      <div class="w-4 h-px bg-n-weak my-1" />
      <template v-for="item in railItems" :key="item.name">
        <button
          type="button"
          class="flex shrink-0 items-center justify-center rounded-lg p-0 size-8 xl:size-10 text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12 transition-colors duration-150"
          :class="{
            'bg-n-alpha-2 text-n-slate-12 font-medium shadow-sm':
              activePanel === item.name,
          }"
          :title="$t(item.titleKey)"
          :aria-label="$t(item.titleKey)"
          @click="openRailPanel(item.name)"
        >
          <span :class="item.icon" class="size-4 shrink-0" aria-hidden="true" />
        </button>
      </template>

      <!-- Reminders (moved from the header) -->
      <span class="relative inline-flex">
        <button
          type="button"
          class="flex shrink-0 items-center justify-center rounded-lg p-0 size-8 xl:size-10 text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12 transition-colors duration-150"
          :class="{
            'bg-n-alpha-2 text-n-slate-12 font-medium shadow-sm':
              showReminderDialog,
          }"
          :title="$t('TELETEL_REMINDERS.HEADER_TOOLTIP')"
          :aria-label="$t('TELETEL_REMINDERS.HEADER_TOOLTIP')"
          @click="openRailPanel('reminders')"
        >
          <span
            class="i-lucide-alarm-clock size-4 shrink-0"
            aria-hidden="true"
          />
        </button>
        <span
          v-if="reminderCount"
          class="absolute -top-1 ltr:-right-1 rtl:-left-1 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-semibold text-white"
          :class="reminderDueCount ? 'bg-n-ruby-9 animate-pulse' : 'bg-n-brand'"
        >
          {{ reminderCount }}
        </span>
      </span>
    </div>

    <!-- Bottom Actions: Mute & Send Transcript -->
    <div
      class="flex flex-col items-center gap-1 w-full mt-auto pt-2 border-t border-n-weak"
    >
      <button
        type="button"
        class="flex shrink-0 items-center justify-center rounded-lg p-0 size-8 xl:size-10 text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12 transition-colors duration-150"
        :class="{ 'text-n-ruby-9': isMuted }"
        :title="
          isMuted
            ? $t('CONTACT_PANEL.UNMUTE_CONTACT')
            : `${$t('CONTACT_PANEL.MUTE_CONTACT')} — ${$t('CONTACT_PANEL.MUTE_HINT')}`
        "
        :aria-label="
          isMuted
            ? $t('CONTACT_PANEL.UNMUTE_CONTACT')
            : $t('CONTACT_PANEL.MUTE_CONTACT')
        "
        @click="toggleMute"
      >
        <span
          :class="isMuted ? 'i-lucide-volume-1' : 'i-lucide-volume-off'"
          class="size-4 shrink-0"
        />
      </button>
      <button
        type="button"
        class="flex shrink-0 items-center justify-center rounded-lg p-0 size-8 xl:size-10 text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12 transition-colors duration-150"
        :title="$t('CONTACT_PANEL.SEND_TRANSCRIPT')"
        :aria-label="$t('CONTACT_PANEL.SEND_TRANSCRIPT')"
        @click="toggleEmailModal"
      >
        <span class="i-lucide-share size-4 shrink-0" />
      </button>
    </div>
  </nav>
</template>
